import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Application,
  AuthSession,
  CreateApplication,
  CreateRelease,
  Release,
} from "@updaterelay/contracts";
import {
  AlertTriangle,
  ArrowUpRight,
  Check,
  Clipboard,
  Cloud,
  Code2,
  Ellipsis,
  LogOut,
  PackagePlus,
  Pencil,
  Plus,
  RadioTower,
  Rocket,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { API_BASE_URL, api } from "../lib/api";
import { ApplicationDialog } from "./ApplicationDialog";
import { Brand } from "./Brand";
import { ReleaseDialog } from "./ReleaseDialog";

interface DashboardProps {
  session: AuthSession;
}

function mutationMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  return error instanceof Error ? error.message : "The request could not be completed.";
}

function formatDate(value: string | null): string {
  if (!value) return "Draft";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function targetLabel(release: Release): string {
  const platform = release.platform === "any" ? "all platforms" : release.platform;
  const architecture = release.architecture === "any" ? "all architectures" : release.architecture;
  return `${platform} · ${architecture}`;
}

export function Dashboard({ session }: DashboardProps) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [applicationDialog, setApplicationDialog] = useState<Application | "new" | null>(null);
  const [releaseDialog, setReleaseDialog] = useState<Release | "new" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const applicationsQuery = useQuery({
    queryKey: ["applications"],
    queryFn: api.getApplications,
  });
  const applications = applicationsQuery.data?.data ?? [];
  const selectedApplication =
    applications.find((application) => application.id === selectedId) ?? applications[0] ?? null;

  useEffect(() => {
    if (selectedApplication && selectedApplication.id !== selectedId) {
      setSelectedId(selectedApplication.id);
    }
  }, [selectedApplication, selectedId]);

  const releasesQuery = useQuery({
    queryKey: ["releases", selectedApplication?.id],
    queryFn: () => api.getReleases(selectedApplication?.id ?? ""),
    enabled: Boolean(selectedApplication),
  });
  const releases = releasesQuery.data?.data ?? [];

  const applicationMutation = useMutation({
    mutationFn: async ({
      application,
      input,
    }: {
      application: Application | null;
      input: CreateApplication;
    }) =>
      application
        ? api.updateApplication(application.id, input, session.csrfToken)
        : api.createApplication(input, session.csrfToken),
    onSuccess: async ({ data }) => {
      setSelectedId(data.id);
      setApplicationDialog(null);
      setNotice(null);
      await queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });

  const deleteApplicationMutation = useMutation({
    mutationFn: (application: Application) =>
      api.deleteApplication(application.id, session.csrfToken),
    onSuccess: async () => {
      setSelectedId(null);
      setNotice(null);
      await queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
    onError: (error) => setNotice(mutationMessage(error) ?? "Application deletion failed."),
  });

  const releaseMutation = useMutation({
    mutationFn: async ({ release, input }: { release: Release | null; input: CreateRelease }) => {
      if (!selectedApplication) throw new Error("Select an application first.");
      return release
        ? api.updateRelease(selectedApplication.id, release.id, input, session.csrfToken)
        : api.createRelease(selectedApplication.id, input, session.csrfToken);
    },
    onSuccess: async () => {
      setReleaseDialog(null);
      setNotice(null);
      await queryClient.invalidateQueries({ queryKey: ["releases", selectedApplication?.id] });
    },
  });

  const releaseStatusMutation = useMutation({
    mutationFn: async (release: Release) => {
      if (!selectedApplication) throw new Error("Select an application first.");
      return api.updateRelease(
        selectedApplication.id,
        release.id,
        { publishedAt: release.publishedAt ? null : new Date().toISOString() },
        session.csrfToken,
      );
    },
    onSuccess: async () => {
      setNotice(null);
      await queryClient.invalidateQueries({ queryKey: ["releases", selectedApplication?.id] });
    },
    onError: (error) => setNotice(mutationMessage(error) ?? "Release status could not be changed."),
  });

  const deleteReleaseMutation = useMutation({
    mutationFn: async (release: Release) => {
      if (!selectedApplication) throw new Error("Select an application first.");
      return api.deleteRelease(selectedApplication.id, release.id, session.csrfToken);
    },
    onSuccess: async () => {
      setNotice(null);
      await queryClient.invalidateQueries({ queryKey: ["releases", selectedApplication?.id] });
    },
    onError: (error) => setNotice(mutationMessage(error) ?? "Release deletion failed."),
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.logout(session.csrfToken),
    onSuccess: ({ logoutUrl }) => window.location.assign(logoutUrl),
    onError: (error) => setNotice(mutationMessage(error) ?? "Sign-out failed."),
  });

  const updateEndpoint = useMemo(() => {
    if (!selectedApplication) return "";
    return `${API_BASE_URL}/v1/apps/${selectedApplication.slug}/update?current_version=1.0.0&channel=stable&platform=windows&architecture=x64`;
  }, [selectedApplication]);

  const publishedCount = releases.filter((release) => release.publishedAt).length;

  return (
    <div className="app-shell">
      <header className="topbar">
        <Brand compact />
        <div className="topbar__environment">
          <span className="status-dot" />
          Production relay
        </div>
        <div className="topbar__account">
          <span className="account-avatar" aria-hidden="true">
            {(session.user.firstName?.[0] ?? session.user.email[0] ?? "U").toUpperCase()}
          </span>
          <span className="topbar__identity">
            <strong>{session.user.firstName ?? "Administrator"}</strong>
            <small>{session.user.email}</small>
          </span>
          <button
            className="icon-button icon-button--dark"
            type="button"
            aria-label="Sign out"
            title="Sign out"
            disabled={logoutMutation.isPending}
            onClick={() => logoutMutation.mutate()}
          >
            <LogOut size={17} />
          </button>
        </div>
      </header>

      {notice ? (
        <div className="global-notice" role="alert">
          <AlertTriangle size={17} /> {notice}
          <button type="button" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="workspace">
        <aside className="application-rail">
          <div className="application-rail__header">
            <div>
              <p className="eyebrow">Signals</p>
              <h1>Applications</h1>
            </div>
            <button
              className="icon-button icon-button--accent"
              type="button"
              aria-label="Add application"
              onClick={() => setApplicationDialog("new")}
            >
              <Plus size={18} />
            </button>
          </div>

          {applicationsQuery.isPending ? (
            <div className="rail-loading" role="status" aria-label="Loading applications">
              <span />
              <span />
              <span />
            </div>
          ) : applicationsQuery.error ? (
            <div className="rail-empty">
              <AlertTriangle size={20} />
              <p>{mutationMessage(applicationsQuery.error)}</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="rail-empty">
              <RadioTower size={22} />
              <p>No signals yet.</p>
              <button
                className="text-button"
                type="button"
                onClick={() => setApplicationDialog("new")}
              >
                Register the first app
              </button>
            </div>
          ) : (
            <nav className="application-list" aria-label="Applications">
              {applications.map((application) => (
                <button
                  className={`application-item ${selectedApplication?.id === application.id ? "is-active" : ""}`}
                  type="button"
                  key={application.id}
                  onClick={() => setSelectedId(application.id)}
                >
                  <span className="application-item__mark">
                    {application.name.slice(0, 2).toUpperCase()}
                  </span>
                  <span>
                    <strong>{application.name}</strong>
                    <small>/{application.slug}</small>
                  </span>
                  <ArrowUpRight size={15} />
                </button>
              ))}
            </nav>
          )}

          <div className="rail-footer">
            <Cloud size={15} />
            <span>D1-backed release metadata</span>
          </div>
        </aside>

        <main className="release-workspace">
          {!selectedApplication ? (
            <section className="empty-workspace">
              <span className="empty-workspace__icon">
                <RadioTower size={26} />
              </span>
              <p className="eyebrow">Awaiting a signal</p>
              <h2>Register an application to start relaying releases.</h2>
              <p>
                Each application gets a stable public endpoint while its release controls remain
                behind WorkOS.
              </p>
              <button
                className="button button--primary"
                type="button"
                onClick={() => setApplicationDialog("new")}
              >
                <Plus size={17} /> Add application
              </button>
            </section>
          ) : (
            <>
              <section className="application-heading">
                <div>
                  <div className="application-heading__meta">
                    <span className="status-pill status-pill--live">
                      <span /> Active signal
                    </span>
                    <span>/{selectedApplication.slug}</span>
                  </div>
                  <h2>{selectedApplication.name}</h2>
                  <p>{selectedApplication.description || "No application description yet."}</p>
                </div>
                <div className="heading-actions">
                  <button
                    className="button button--ghost"
                    type="button"
                    onClick={() => setApplicationDialog(selectedApplication)}
                  >
                    <Pencil size={15} /> Edit
                  </button>
                  <button
                    className="icon-button icon-button--danger"
                    type="button"
                    title="Delete application"
                    aria-label={`Delete ${selectedApplication.name}`}
                    disabled={deleteApplicationMutation.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete ${selectedApplication.name} and all of its release metadata?`,
                        )
                      ) {
                        deleteApplicationMutation.mutate(selectedApplication);
                      }
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </section>

              <section className="endpoint-card">
                <div className="endpoint-card__icon" aria-hidden="true">
                  <Code2 size={20} />
                </div>
                <div className="endpoint-card__body">
                  <div>
                    <p className="eyebrow">Installer handshake</p>
                    <strong>Public update-check endpoint</strong>
                  </div>
                  <code>{updateEndpoint}</code>
                </div>
                <button
                  className="button button--copy"
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(updateEndpoint);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1_600);
                  }}
                >
                  {copied ? <Check size={15} /> : <Clipboard size={15} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </section>

              <section className="release-section">
                <header className="section-heading">
                  <div>
                    <p className="eyebrow">Release ledger</p>
                    <h3>Published signal</h3>
                    <p>
                      {publishedCount} published · {releases.length - publishedCount} draft
                    </p>
                  </div>
                  <button
                    className="button button--primary"
                    type="button"
                    onClick={() => setReleaseDialog("new")}
                  >
                    <PackagePlus size={17} /> Create release
                  </button>
                </header>

                {releasesQuery.isPending ? (
                  <div className="release-loading" role="status" aria-label="Loading releases">
                    <span />
                    <span />
                  </div>
                ) : releasesQuery.error ? (
                  <div className="inline-empty inline-empty--error">
                    <AlertTriangle size={22} />
                    <p>{mutationMessage(releasesQuery.error)}</p>
                  </div>
                ) : releases.length === 0 ? (
                  <div className="inline-empty">
                    <Rocket size={24} />
                    <h4>No releases on the wire.</h4>
                    <p>Create a draft, verify its installer URL, then publish when it is ready.</p>
                    <button
                      className="text-button"
                      type="button"
                      onClick={() => setReleaseDialog("new")}
                    >
                      Create the first release
                    </button>
                  </div>
                ) : (
                  <div className="release-list">
                    {releases.map((release) => (
                      <article className="release-card" key={release.id}>
                        <div className="release-card__version">
                          <span
                            className={`release-state ${release.publishedAt ? "is-published" : ""}`}
                          >
                            {release.publishedAt ? "Published" : "Draft"}
                          </span>
                          <strong>v{release.version}</strong>
                          <small>{formatDate(release.publishedAt)}</small>
                        </div>
                        <div className="release-card__details">
                          <div className="target-row">
                            <span>{release.channel}</span>
                            <span>{targetLabel(release)}</span>
                            {release.mandatory ? (
                              <span className="target-row__mandatory">Mandatory</span>
                            ) : null}
                          </div>
                          <a href={release.installerUrl} target="_blank" rel="noreferrer">
                            {release.installerUrl} <ArrowUpRight size={13} />
                          </a>
                          <p>{release.releaseNotes || "No release notes."}</p>
                        </div>
                        <div className="release-card__actions">
                          <button
                            className="button button--small"
                            type="button"
                            disabled={releaseStatusMutation.isPending}
                            onClick={() => releaseStatusMutation.mutate(release)}
                          >
                            {release.publishedAt ? "Unpublish" : "Publish"}
                          </button>
                          <button
                            className="icon-button"
                            type="button"
                            aria-label={`Edit ${release.version}`}
                            onClick={() => setReleaseDialog(release)}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            className="icon-button"
                            type="button"
                            aria-label={`Delete ${release.version}`}
                            disabled={deleteReleaseMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`Delete release ${release.version}?`)) {
                                deleteReleaseMutation.mutate(release);
                              }
                            }}
                          >
                            <Trash2 size={15} />
                          </button>
                          <Ellipsis className="release-card__grip" size={17} aria-hidden="true" />
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>

      <ApplicationDialog
        open={applicationDialog !== null}
        application={applicationDialog === "new" ? null : applicationDialog}
        busy={applicationMutation.isPending}
        error={mutationMessage(applicationMutation.error)}
        onClose={() => {
          applicationMutation.reset();
          setApplicationDialog(null);
        }}
        onSave={async (input) => {
          await applicationMutation.mutateAsync({
            application: applicationDialog === "new" ? null : applicationDialog,
            input,
          });
        }}
      />

      {selectedApplication ? (
        <ReleaseDialog
          open={releaseDialog !== null}
          release={releaseDialog === "new" ? null : releaseDialog}
          applicationName={selectedApplication.name}
          busy={releaseMutation.isPending}
          error={mutationMessage(releaseMutation.error)}
          onClose={() => {
            releaseMutation.reset();
            setReleaseDialog(null);
          }}
          onSave={async (input) => {
            await releaseMutation.mutateAsync({
              release: releaseDialog === "new" ? null : releaseDialog,
              input,
            });
          }}
        />
      ) : null}
    </div>
  );
}
